import React, { useState, useEffect } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import uuid4 from "uuid4";
import { toast } from "react-toastify";
import {
  getDoc,
  doc,
  setDoc,
  onSnapshot,
  collection,
  getDocs,
  serverTimestamp,
  where,
  query,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "./FirebaseConfig";
import { toastOptions } from "./lib/toastOptions";
import { UserAuth } from "./Context";
import { Modal, Form, Button } from "react-bootstrap";
import useSocketStore from "./StatesStore/Socket";
import InviteLists from "./ShoppingList/InviteLists";
import { listNameExists } from "./lib/listNameExists";
import SavedLists from "./ShoppingList/SavedLists";
import LoadingSpinner from "./UserProfile/LoadingSpinner";
import MyList3 from "./ShoppingList/MyList3";
import useChatClient from "./StatesStore/ChatClient";
import axios from "axios";
import MyList4 from "./ShoppingList/MyList4";
import InviteLists3 from "./ShoppingList/InviteLists3";
import { saveShoppingListToAlgolia } from "./lib/algoliaFunctions";

const ShoppingLists4 = () => {
  // My Shopping list Data
  const [shoppingListData, setShoppingListData] = useState(null);
  // Invited ShoppingList Data
  const [invitedshoppingListData, setInvitedShoppingListData] = useState([]);
  // Current Selected Category
  const [selectedListCategory, setSelectedListCategory] = useState(
    "MyShoppingList"
  );

  // Two categories: true for MyShoppingList, false for Invited Shopping List
  // const checkMyShoppingList = selectedListCategory === "MyShoppingList";

  const categoryChecks = {
    isMyShoppingList: selectedListCategory === "MyShoppingList",
    isInvitedShoppingList: selectedListCategory === "InvitedShoppingList",
    isSavedLists: selectedListCategory === "SavedLists",
  };

  // Used when createing new list
  const [newListName, setNewListName] = useState("");
  // Used to separate the invited list by listId and userId
  // const [invitedList, setInvitedList] = useState([]);
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
    setNewListName("");
    setError("");
  };
  const { user } = UserAuth();
  const location = useLocation();
  const [activeAccordion, setActiveAccordion] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviteListLoading, setInviteListLoading] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const client = useChatClient((state) => state.client);

  useEffect(() => {
    if (location.pathname.startsWith("/shoppingLists/inviteLists")) {
      setSelectedListCategory("InvitedShoppingList");
    } else if (location.pathname.startsWith("/shoppingLists/savedLists")) {
      setSelectedListCategory("SavedLists");
    } else {
      setSelectedListCategory("MyShoppingList");
    }
  }, [location]);

  useEffect(() => {
    const fetchShoppingLists = async () => {
      if (!user) return;
      setLoading(true);
      const userRef = doc(db, "users", user.uid);
      const shoppingListRef = collection(userRef, "shoppingList");

      let productUnsubscribes = [];
      const unsubscribe = onSnapshot(shoppingListRef, async (snapshot) => {
        try {
          const shoppingListPromises = snapshot.docs.map(async (document) => {
            const shoppingList = document.data();
            const dateCreated =
              shoppingList.dateCreated?.seconds || new Date().getTime() / 1000;
            const lastUpdated =
              shoppingList.lastUpdated?.seconds || new Date().getTime() / 1000;

            const productRef = collection(
              shoppingListRef,
              document.id,
              "products"
            );
            const productUnsubscribe = onSnapshot(
              productRef,
              (productSnapshot) => {
                const products = productSnapshot.docs.map((productDoc) => ({
                  id: productDoc.id,
                  ...productDoc.data(),
                }));

                setShoppingListData((prevData) => {
                  const updatedData = prevData?.map((list) => {
                    if (list.name === document.id) {
                      return { ...list, products };
                    }
                    return list;
                  });
                  return updatedData?.sort(
                    (a, b) =>
                      b.products.length - a.products.length ||
                      b.dateCreated - a.dateCreated
                  );
                });
              }
            );

            productUnsubscribes.push(productUnsubscribe);

            const productSnapshot = await getDocs(productRef);
            const products = productSnapshot.docs.map((productDoc) => ({
              id: productDoc.id,
              ...productDoc.data(),
            }));

            return {
              ...shoppingList,
              id: document.id,
              name: shoppingList.listName,
              dateCreated,
              lastUpdated,
              products,
              userId: user?.uid,
              listId: shoppingList.listId,
              sizes: shoppingList.sizes,
              visibility: shoppingList.visibility,
            };
          });

          const shoppingListDataArray = await Promise.all(shoppingListPromises);
          shoppingListDataArray.sort((a, b) =>
            // b.products.length - a.products.length ||
            b.lastUpdated && a.lastUpdated
              ? b.lastUpdated - a.lastUpdated || a.dateCreated - b.dateCreated
              : a.dateCreated - b.dateCreated
          );
          setShoppingListData(shoppingListDataArray);
          setLoading(false);
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      });

      return () => {
        unsubscribe();
        productUnsubscribes.forEach((unsubscribe) => unsubscribe());
      };
    };

    const fetchInvitedShoppingLists = async () => {
      if (!user) return;

      const invitedListRef = collection(db, "users", user.uid, "invitedList");
      const invitedListUnsubscribes = [];

      const unsubscribe = onSnapshot(invitedListRef, async (snapshot) => {
        try {
          setInviteListLoading(true);
          const invitedLists = snapshot.docs.map((doc) => ({
            listId: doc.id,
            userId: doc.data().userId,
            role: doc.data().role,
          }));

          const testShoppingLists = await Promise.all(
            invitedLists.map(async ({ listId, userId, role }) => {
              const shoppingListRef = collection(
                db,
                "users",
                userId,
                "shoppingList"
              );
              const q = query(shoppingListRef, where("listId", "==", listId));
              const querySnapshot = await getDocs(q);

              if (querySnapshot.empty) return null;

              const shoppingListSnapshot = querySnapshot.docs[0];
              const shoppingListData = shoppingListSnapshot.data();

              const productRef = collection(
                shoppingListRef,
                shoppingListSnapshot.id,
                "products"
              );
              const productUnsubscribe = onSnapshot(
                productRef,
                (productSnapshot) => {
                  const products = productSnapshot.docs.map((productDoc) => ({
                    id: productDoc.id,
                    ...productDoc.data(),
                  }));

                  setInvitedShoppingListData((prevData) => {
                    const updatedData = prevData.map((list) => {
                      if (list.name === shoppingListSnapshot.id) {
                        return { ...list, products };
                      }
                      return list;
                    });
                    return updatedData;
                  });
                }
              );

              invitedListUnsubscribes.push(productUnsubscribe);

              const productSnapshot = await getDocs(productRef);
              const products = productSnapshot.docs.map((productDoc) => ({
                id: productDoc.id,
                ...productDoc.data(),
              }));

              return {
                id: shoppingListSnapshot.id,
                name: shoppingListSnapshot.data().listName,
                dateCreated:
                  shoppingListData.dateCreated?.seconds ||
                  new Date().getTime() / 1000,
                products,
                listId: shoppingListData.listId,
                sizes: shoppingListData.sizes,
                userId: userId,
                role: role,
                lastUpdated:
                  shoppingListData.lastUpdated?.seconds ||
                  new Date().getTime() / 1000,
                visibility: shoppingListData.visibility,
              };
            })
          );

          let filteredShoppingLists = testShoppingLists.filter(
            (list) => list !== null
          );

          await Promise.all(
            filteredShoppingLists.map(async (list) => {
              const userRef = doc(db, "users", list?.userId);
              const userData = await getDoc(userRef);
              const userCompleteData = userData?.data();
              list.userName = userCompleteData?.userName;
              list.photo = userCompleteData?.photo;
            })
          );

          const urlListId = location.pathname.split("/").pop();
          if (urlListId && urlListId !== "inviteLists") {
            filteredShoppingLists.sort((a, b) => {
              if (a.listId === urlListId) return -1;
              if (b.listId === urlListId) return 1;
              return (
                b.lastUpdated - a.lastUpdated || a.dateCreated - b.dateCreated
              );
            });
          } else {
            filteredShoppingLists.sort(
              (a, b) =>
                b.lastUpdated - a.lastUpdated || a.dateCreated - b.dateCreated
            );
          }

          setInvitedShoppingListData(filteredShoppingLists);
          setInviteListLoading(false);
        } catch (error) {
          console.error(error);
        } finally {
          setInviteListLoading(false);
        }
      });

      return () => {
        unsubscribe();
        invitedListUnsubscribes.forEach((unsubscribe) => unsubscribe());
      };
    };
    document.body.scrollIntoView({ behavior: "smooth" });
    document.body.scrollTo(0, 0);
    // setLoading(true);
    fetchShoppingLists();
    fetchInvitedShoppingLists();
    // setLoading(false);
  }, [user]);

  const handleCreateNewList = async () => {
    const listName = newListName.trim();
    if (newListName?.length === 0 || error || newListName[0] === " ") return;
    else if (/^\d+$/.test(newListName)) {
      setError("List name cannot be completely numeric");
      return;
    } else if (/[!@#$%^&*,.?":{}|<>\=+\;()/\\[\]]/.test(newListName)) {
      setError("List name cannot contain special characters");
      return;
    } else if (await listNameExists(user, listName)) {
      setError("List name already exists");
      return;
    }
    try {
      setLoadingButton(true);
      if (listName) {
        const capitalizedListName =
          listName.charAt(0).toUpperCase() + listName.slice(1);
        const response = await axios.post(
          `${process.env.REACT_APP_GEMINI}/getTags`,
          {
            text: capitalizedListName,
          }
        );

        const tags = response.data.result;
        const newListId = await addToSavedList(capitalizedListName, "", tags);
        setShoppingListData([
          ...shoppingListData,
          {
            name: capitalizedListName,
            products: [],
            dateCreated: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            listName: listName,
            listId: newListId,
            visibility: "Public",
            tags: tags,
          },
        ]);
        if (newListId) {
          // Create a team channel for the newly created list
          await createTeamChannel(newListId, capitalizedListName, user?.uid);

          // Set the user as admin in the channel
          // await channel.addMembers([user?.uid], { text: `${user.displayName} created the list.`, role: "admin" });
        }
        toast.success(
          ` ${capitalizedListName} Shopping List has been created`,
          toastOptions
        );

        setNewListName("");
        handleClose();
      }
    } catch (error) {
      console.error("Error creating New shopping list:", error.message);
    } finally {
      setLoadingButton(false);
    }
  };

  const addToSavedList = async (listName, size, tags = []) => {
    if (!user) return;

    try {
      const listDocRef = doc(db, "users", user?.uid, "shoppingList", listName);

      // Check if the list document exists
      const listDoc = await getDoc(listDocRef);
      let listId;
      if (!listDoc.exists()) {
        // If the list does not exist, create it
        listId = uuid4();
        await setDoc(listDocRef, {
          dateCreated: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          listName: listName,
          listId: listId,
          visibility: "Public",
          tags: tags,
        });

        await saveShoppingListToAlgolia(user, listName);
      } else {
        // If the list already exists, use its existing listId
        listId = listDoc.data().listId;
      }

      // Update the local state
      console.log("Product added to the list successfully!");
      return listId;
    } catch (error) {
      console.error("Error adding product to the list:", error.message);
      return null;
    }
  };
  const createTeamChannel = async (listId, listName, creatorId) => {
    try {
      const channel = await client.channel("team", listId, {
        name: listName,
        image: `https://ui-avatars.com/api/?name=${listName}`,
      });

      await channel.create();
      await channel.addMembers([
        { user_id: creatorId, channel_role: "channel_moderator" },
      ]);
      console.log("Team channel created successfully:", channel.id);
      return channel;
    } catch (error) {
      console.error("Error creating team channel:", error);
      throw error;
    }
  };
  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      {/* <div className="col-md-8 col-lg-6 vstack gap-3 text-body mt-0 mt-lg-5 px-3 px-lg-0 me-2"> */}
      <div
        // className="vstack text-body px-0 ms-0 ms-lg-1 border rounded-2"
        className="vstack text-body px-0 m-0 rounded-0"
      >
        <style jsx>
          {`
            .linkStyle {
              font-family: Inter;
              font-size: 20px;
              line-height: 100%;
              text-align: center;
              display: inline-block;
              padding-bottom: 6px;
            }
          `}
        </style>
        {/* Mobile */}
        {activeAccordion.length === 0 && (
          <div className="d-block d-sm-none card card-header rounded-0 p-0 mt-2 border-0 stickyHeaderMobile mb-0 px-2">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <ul className="nav nav-bottom-line border-0 d-flex align-items-center gap-4">
                <li className="fs-5 nav-item mb-0 ">
                  <Link
                    className={`nav-link fw-light py-0 px-0 ${
                      categoryChecks.isMyShoppingList ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedListCategory("MyShoppingList");
                    }}
                    to="/shoppingLists"
                  >
                    My Lists
                  </Link>
                </li>
                <li className="fs-5 nav-item mb-0">
                  <Link
                    className={`nav-link fw-light py-0 px-0 ${
                      categoryChecks.isInvitedShoppingList ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedListCategory("InvitedShoppingList");
                    }}
                    to="/shoppingLists/inviteLists"
                  >
                    <span>Invited Lists</span>
                  </Link>
                </li>
              </ul>
              {categoryChecks.isMyShoppingList && (
                <li
                  onClick={() => setShow(true)}
                  className="btn btn-sm btn-primary p-2 py-1 rounded-4"
                >
                  <i className="fas fa-plus fs-6"></i>
                </li>
              )}
            </div>
          </div>
        )}

        {/* Desktop */}
        <div
          className="d-none d-sm-flex flex-row justify-content-between align-items-center stickyHeader bg-mode"
          style={{
            marginTop: "20px",
            fontFamily: "Inter",
            padding: "10px 10px",
          }}
        >
          <div className="d-flex align-items-center" style={{ gap: "60px" }}>
            <Link
              to="/shoppingLists"
              onClick={() => {
                setSelectedListCategory("MyShoppingList");
              }}
              className="linkStyle"
              style={{
                borderBottom: categoryChecks.isMyShoppingList
                  ? `2px solid rgb(0, 95, 218)`
                  : "",
                color: categoryChecks.isMyShoppingList ? "#005FDA" : "#667085",
                fontWeight: categoryChecks.isMyShoppingList ? 500 : 400,
              }}
            >
              My List
            </Link>
            <Link
              to="/shoppingLists/inviteLists"
              onClick={() => {
                setSelectedListCategory("InvitedShoppingList");
              }}
              className="linkStyle"
              style={{
                borderBottom: categoryChecks.isInvitedShoppingList
                  ? `2px solid rgb(0, 95, 218)`
                  : "",
                color: categoryChecks.isInvitedShoppingList
                  ? "#005FDA"
                  : "#667085",
                fontWeight: categoryChecks.isInvitedShoppingList ? 500 : 400,
              }}
            >
              Invited List
            </Link>
          </div>
          <div
            style={{
              visibility: categoryChecks.isMyShoppingList
                ? "visible"
                : "hidden",
              borderRadius: "12px",
              height: "42px",
              padding: "0px 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "1px solid #005FDA",
            }}
            onClick={() => setShow(true)}
          >
            <div
              style={{
                fontWeight: 500,
                color: "#005FDA",
                fontSize: "18px",
              }}
            >
              Create New List
            </div>
          </div>
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <MyList4
                shoppingListData={shoppingListData}
                setShoppingListData={setShoppingListData}
                activeAccordion={activeAccordion}
                setActiveAccordion={setActiveAccordion}
                loading={loading}
                setLoading={setLoading}
              />
            }
          />
          <Route
            path="/inviteLists/*"
            element={
              <InviteLists3
                invitedshoppingListData={invitedshoppingListData}
                setInvitedShoppingListData={setInvitedShoppingListData}
                activeAccordion={activeAccordion}
                setActiveAccordion={setActiveAccordion}
                loading={inviteListLoading}
                setLoading={setInviteListLoading}
                // invitedList={invitedList}
              />
            }
          />
          <Route path="/savedLists/*" element={<SavedLists />} />
        </Routes>
      </div>
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Body>
          <div className="d-flex flex-column mb-3">
            <div className="d-flex w-100 justify-content-between">
              <div className="h4">Create new list</div>
              <button
                type="button"
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: "25px",
                  height: "25px",
                  border: "1px solid red",
                  borderRadius: "50%",
                  color: "red",
                  fontSize: "16px",
                  fontWeight: "lighter",
                  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                  cursor: "pointer",
                  backgroundColor: "white",
                }}
                onClick={handleClose}
              >
                x
              </button>
            </div>
            <p className="m-0">
              Enter a name for your new list, and save the product to it.
            </p>
          </div>
          <Form
            className="d-flex flex-column w-100 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateNewList();
            }}
          >
            <div className="p-0 w-100">
              <Form.Control
                type="text"
                className="rounded-3"
                placeholder="Create new list"
                value={newListName}
                onChange={(e) => {
                  setNewListName(e.target.value);
                  setError("");
                  if (/^\d+$/.test(e.target.value)) {
                    setError("List name cannot be completely numeric");
                  } else if (
                    /[!@#$%^&*,.?":{}|<>\=+\;()/\\[\]]/.test(e.target.value)
                  ) {
                    setError("List name cannot contain special characters");
                  }
                }}
              />
            </div>
            <div className="p-0">
              <Button
                variant="primary"
                type="submit"
                className="container rounded-3"
                data-bs-dismiss="modal"
                disabled={
                  newListName?.length === 0 ||
                  newListName[0] === " " ||
                  error === true
                }
              >
                {loadingButton ? (
                  <div
                    className="spinner-border text-white"
                    style={{ width: "1rem", height: "1rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
          {error && <div className="text-danger">{error}</div>}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ShoppingLists4;
